import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Ruler, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Stick Roofing",
  "Roof Trusses",
  "Timber Framing",
  "Roof Carpentry & Structural Timber",
  "New Build",
  "Renovation / Extension",
  "Other",
];

const inputCls =
  "rounded-none bg-obsidian border-neutral-700 text-white placeholder:text-neutral-600 h-12 focus-visible:ring-timber focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-timber";

const initial = { name: "", phone: "", email: "", suburb: "", project_type: "", message: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_type) {
      toast.error("Please select a project type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Request received! We'll be in touch shortly to arrange your free measure & quote.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please call us on 0408 166 415.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="border-b border-neutral-800">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-sm uppercase tracking-[0.3em] text-timber mb-4">Free Measure &amp; Quote</p>
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl md:text-5xl lg:text-6xl text-white leading-[0.9]">
            Get it measured.<br />
            <span className="text-outline">Get it moving.</span>
          </h2>
          <p className="text-neutral-300 text-base md:text-lg leading-relaxed mt-8 max-w-md">
            Send us your project details and we'll arrange a free measure and
            quote. Quick turnaround, quality work, no unnecessary delays — and
            no upfront payment.
          </p>
          <ul className="mt-10 space-y-5">
            <li className="flex items-center gap-4">
              <Phone size={20} className="text-timber" />
              <a data-testid="contact-phone-link" href="tel:0408166415" className="text-white hover:text-timber transition-colors duration-300 text-lg font-display tracking-wider">
                0408 166 415
              </a>
            </li>
            <li className="flex items-center gap-4">
              <Mail size={20} className="text-timber" />
              <a data-testid="contact-email-link" href="mailto:carpentry.specialists@outlook.com" className="text-white hover:text-timber transition-colors duration-300 break-all">
                carpentry.specialists@outlook.com
              </a>
            </li>
            <li className="flex items-center gap-4">
              <MapPin size={20} className="text-timber" />
              <span data-testid="contact-location" className="text-neutral-300">Perth, Western Australia</span>
            </li>
          </ul>
        </motion.div>

        <motion.form
          data-testid="quote-form"
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="border border-neutral-800 bg-surface p-8 md:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Input data-testid="quote-form-name" required minLength={2} placeholder="Your name *" value={form.name} onChange={set("name")} className={inputCls} />
            <Input data-testid="quote-form-phone" required minLength={6} placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Input data-testid="quote-form-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
            <Input data-testid="quote-form-suburb" required minLength={2} placeholder="Suburb *" value={form.suburb} onChange={set("suburb")} className={inputCls} />
          </div>
          <Select value={form.project_type} onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}>
            <SelectTrigger data-testid="quote-form-project-type" className={`${inputCls} data-[placeholder]:text-neutral-600`}>
              <SelectValue placeholder="Project type *" />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-obsidian border-neutral-700 text-white">
              {PROJECT_TYPES.map((t) => (
                <SelectItem key={t} value={t} data-testid={`quote-form-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="focus:bg-timber focus:text-obsidian">
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            data-testid="quote-form-message"
            placeholder="Tell us about your project — plans, stage of build, timeline…"
            value={form.message}
            onChange={set("message")}
            rows={5}
            className={`${inputCls} h-auto resize-none`}
          />
          <Button
            data-testid="quote-form-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-none bg-timber hover:bg-timber-hover text-obsidian font-display font-bold uppercase tracking-widest text-base h-14 transition-colors duration-300"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              <span className="flex items-center gap-3">
                <Ruler size={18} /> Request Free Measure &amp; Quote
              </span>
            )}
          </Button>
          <p className="text-neutral-500 text-xs text-center">No upfront payment. No obligation. Perth-wide.</p>
        </motion.form>
      </div>
    </section>
  );
}
